import {
  GetFilterCaseIdApiArg,
  GetFilterTmInfringementDomainsApiArg,
  GetFilterTrademarkApiArg,
  useGetFilterCaseIdQuery,
  useGetFilterCountryQuery,
  useGetFilterProjectsQuery,
  useGetFilterTmInfringementDomainsQuery,
  useGetFilterTrademarkQuery,
} from '../../../../app/api'
import { Controller, useForm } from 'react-hook-form'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import {
  selectTrademarkInfringementsFilters,
  selectTrademarkInfringementsProject,
  selectTrademarkTabCount,
  setExpandedRows,
  setTrademarkInfringementsFilters,
  setTrademarkInfringementsOnlyRegistered,
  setTrademarkInfringementsProject,
} from '../../model/trademarkInfringementsSlice'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import { DateSelect } from '../../../../shared/components/forms/date-select/DateSelect'
import moment from 'moment/moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import {
  BpSearchInput,
  IOption,
} from '../../../../shared/components/forms/search-input/BpSearchInput'
import { BpTabs } from '../../../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../../../shared/components/utility/tabs/tab/BpTab'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { ITrademarkInfringementsFilters } from '../../model/types/TrademarkInfringementsFilters.interface'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import { Switch } from 'antd'
import { TrademarkInfringementsDomainSummary } from '../trademark-infringements-domain-summary/TrademarkInfringementsDomainSummary'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<ITrademarkInfringementsFilters> = {
  country: '',
  site: '',
  trademark: '',
  caseId: '',
  showWhitelisted: false,
}

export const Filters = ({
  searchParams,
  setSearchParams,
}: IWithSearchParamsProps<ITrademarkInfringementsFilters>) => {
  const { t } = useTranslation()
  const filters = useAppSelector(selectTrademarkInfringementsFilters)
  const {
    project,
    country,
    site,
    dateRange,
    trademark,
    caseId,
    showWhitelisted,
    tab,
    onlyRegistered,
    status,
  } = filters
  const tabCount = useAppSelector(selectTrademarkTabCount)
  const {
    is_demo: isDemo,
    has_trademarks: hasTrademarks,
    has_active_trademarks: hasActiveTrademarks,
    has_tm_infringements: hasTmInfringements,
  } = useAppSelector(selectTrademarkInfringementsProject)
  const showStub = !hasTrademarks || !hasActiveTrademarks || !hasTmInfringements
  const dispatch = useAppDispatch()

  const {
    control,
    setValue,
    getValues,
    formState: { defaultValues },
  } = useForm<ITrademarkInfringementsFilters>({
    mode: 'onChange',
    defaultValues: {
      project,
      country,
      site,
      dateRange,
      trademark,
      caseId,
      showWhitelisted,
      tab,
      ...searchParams,
    },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery(
    {
      projectId: project ? +project : undefined,
    },
    {
      skip: !project,
    },
  )

  const {
    data: domains,
    isFetching: domainsFetching,
    isLoading: domainsLoading,
  } = useGetFilterTmInfringementDomainsQuery(
    {
      search: site,
      projectId: project,
      countryId: country,
      date: dateRange,
      trademarkId: trademark,
      showWhitelisted: showWhitelisted,
      tab: tab,
      onlyRegistered: onlyRegistered,
      ...(tab === 'in_progress' && { status: status }),
    } as unknown as GetFilterTmInfringementDomainsApiArg,
    { skip: !project },
  )

  const {
    data: trademarks,
    isFetching: trademarksFetching,
    isLoading: trademarksLoading,
  } = useGetFilterTrademarkQuery(
    {
      projectId: project ? +project : undefined,
    } as GetFilterTrademarkApiArg,
    {
      skip: !project,
    },
  )

  const {
    data: caseIds,
    isFetching: caseIdsFetching,
    isLoading: caseIdsLoading,
  } = useGetFilterCaseIdQuery(
    {
      search: caseId,
      projectId: project ? +project : undefined,
    } as GetFilterCaseIdApiArg,
    { skip: !project },
  )

  const tabs = [
    { text: t('pages.trademarkInfringements.toSubmit'), value: 'to_submit' },
    { text: t('pages.trademarkInfringements.inProgress'), value: 'in_progress' },
    { text: t('pages.trademarkInfringements.resolved'), value: 'removed' },
  ] as const

  const applyFilter = () => {
    const values = getValues()

    dispatch(setTrademarkInfringementsFilters(values))
    if (setSearchParams) setSearchParams(values)
  }

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      applyFilter()
    },
  })

  return (
    <div className={'flex flex-col gap-[24px]'}>
      <div className={'flex flex-col gap-[8px] [container:filters/inline-size]'}>
        <div
          className={
            'flex flex-col items-center gap-[8px] [@container_filters_(width>=1199px)]:flex-row'
          }
        >
          <div
            className={
              'grid w-full grid-cols-1 gap-[8px] [@container_filters_(568px<=width<1199px)]:grid-cols-2 [@container_filters_(width>=1199px)]:grid-cols-4'
            }
          >
            <Controller
              render={({ field: { onChange, ...fieldProps } }) => {
                const defaultValue =
                  defaultValues?.project ??
                  project ??
                  projects?.find((option) => !option.is_demo)?.value ??
                  projects?.[0]?.value

                return (
                  <BpSelect
                    {...fieldProps}
                    Icon={ProjectIcon}
                    bottomSection={<AddProjectButton />}
                    defaultValue={defaultValue}
                    disabled={projectsLoading || projectsFetching}
                    fieldSize='lg'
                    loading={projectsLoading || projectsFetching}
                    options={projects}
                    title={t('fields.project')}
                    onChange={(e) => {
                      onChange(e)

                      const option = projects?.find((option) => option.value === e.target.value)

                      if (option) {
                        dispatch(setTrademarkInfringementsProject(option))
                      }

                      applyFilter()
                    }}
                  ></BpSelect>
                )
              }}
              control={control}
              name='project'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => {
                return (
                  <BpSelect
                    {...fieldProps}
                    multiple
                    Icon={CountryIcon}
                    bottomSection={<EditCountriesButton project={{ value: project, isDemo }} />}
                    disabled={countriesLoading || countriesFetching}
                    fieldSize='lg'
                    loading={countriesLoading || countriesFetching}
                    optionRender={renderCountryOption}
                    options={countries}
                    title={t('fields.country')}
                    value={value?.split(',')}
                    onChange={(e) => {
                      onChange(
                        [].slice
                          .call(e.target.selectedOptions)
                          .map((o: IOption) => o.value)
                          .join(','),
                      )
                    }}
                    onDropClose={applyFilter}
                  ></BpSelect>
                )
              }}
              control={control}
              name='country'
            />
            <Controller
              render={({ field: { onChange, ...fieldProps } }) => (
                <BpSearchInput
                  {...fieldProps}
                  disabled={domainsLoading || domainsFetching}
                  fieldSize='lg'
                  loading={domainsLoading || domainsFetching}
                  options={domains}
                  placeholder={t('fields.domain')}
                  onChange={(e) => {
                    onChange(e)
                    applyFilter()
                  }}
                ></BpSearchInput>
              )}
              control={control}
              name='site'
            />
            <Controller
              render={({ field: { onChange, value } }) => {
                return (
                  <DateSelect
                    value={[
                      moment(value, DEFAULT_DATE_FORMAT),
                      moment(value, DEFAULT_RANGE_FORMAT),
                    ]}
                    onChange={(_, value) => {
                      onChange(`${value[0]} - ${value[1]}`)
                      applyFilter()
                    }}
                  />
                )
              }}
              control={control}
              name='dateRange'
            />
          </div>
          <div
            className={
              'w-full [@container_filters_(width>=1199px)]:w-fit lg:[@container_filters_(width<1199px)]:pr-[calc(50%+4px)]'
            }
          >
            <FilterDrop
              values={getValues(['trademark', 'caseId', 'showWhitelisted'])}
              onClear={() => {
                setValue('trademark', '')
                setValue('caseId', '')
                setValue('showWhitelisted', false)
              }}
              onHide={() => restoreAppliedValues(getValues, setValue, filters)}
              onSubmit={applyFilter}
            >
              <Controller
                render={({ field }) => (
                  <BpSelect
                    {...field}
                    disabled={trademarksLoading || trademarksFetching}
                    label={t('fields.trademark')}
                    loading={trademarksLoading || trademarksFetching}
                    options={trademarks}
                    placeholder={t('fields.other.search')}
                  />
                )}
                control={control}
                name='trademark'
              />
              <Controller
                render={({ field }) => (
                  <BpSearchInput
                    {...field}
                    label={t('fields.caseId')}
                    loading={caseIdsLoading || caseIdsFetching}
                    options={caseIds}
                    placeholder={t('fields.other.search')}
                  ></BpSearchInput>
                )}
                control={control}
                name='caseId'
              />
              <Controller
                render={({ field }) => (
                  <div className={'flex justify-between gap-[15px] pb-[24px]'}>
                    <span className={'text-[15px] leading-[24px] text-focus-500'}>
                      {t('pages.trademarkInfringements.showWhitelistedDomains')}
                    </span>
                    <Switch {...field} defaultChecked={showWhitelisted} />
                  </div>
                )}
                control={control}
                name='showWhitelisted'
              />
            </FilterDrop>
          </div>
        </div>
        {(trademark || caseId) && (
          <div className={'px-[4px]'}>
            <FilterChips
              clearCallBack={(field) => {
                setValue(field as 'trademark' | 'caseId', '')
                applyFilter()
              }}
              items={[
                {
                  title: t('fields.trademark'),
                  field: 'trademark',
                  value: trademark,
                  data: trademarks,
                  type: 'select',
                },
                {
                  title: t('fields.caseId'),
                  field: 'caseId',
                  value: caseId,
                },
              ]}
              applyCallBack={applyFilter}
            />
          </div>
        )}
      </div>
      <TrademarkInfringementsDomainSummary />
      {!showStub && (
        <div>
          <BpTabs
            active={tabs.find((value) => value.value === searchParams?.tab)?.text}
            size={'md'}
            onToggle={(e) => {
              const value = tabs.find((value) => value.text === e)?.value

              if (e !== tab && value) {
                dispatch(setTrademarkInfringementsOnlyRegistered(false))
                dispatch(setExpandedRows([]))

                setValue('tab', value)
                applyFilter()
              }
            }}
          >
            {tabs.map(({ text, value }) => (
              <BpTab key={value} counter={tabCount[value]} title={text} />
            ))}
          </BpTabs>
        </div>
      )}
    </div>
  )
}

export const TrademarkInfringementsFilters = withSearchParams<
  IWithSearchParamsProps<ITrademarkInfringementsFilters>,
  ITrademarkInfringementsFilters
>(Filters, selectTrademarkInfringementsFilters)
